import React, { FC, useCallback, useContext, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { CloseDialogFunc } from '../../hooks/useCloseDialog';
import { IProductPackageFormPayload, ProductPackageForm } from './ProductPackageForm';
import { useForm } from 'react-hook-form';
import { apiProductPackage } from '../../resources/api/apiProductPackage';
import { useApiErrorHandler } from '../../hooks/useApiErrorHandler';
import { ProductPackagesFetcher } from '../../fetchers/ProductPackagesFetcher';
import { useTranslation } from 'react-i18next';
import { PackagesType } from '../../models/PackagesType';
import { getPackageVariantsPayload } from '../../models/PackageDuration';

interface IProps {
  open: boolean;
  closeDialog: CloseDialogFunc;
  packagesType: PackagesType;
}

const { useCreatePackage } = apiProductPackage;

export const AddProductPackageDialog: FC<IProps> = ({ open, closeDialog, packagesType }) => {
  const form = useForm();
  const { execute: createPackage } = useCreatePackage();
  const handleError = useApiErrorHandler();
  const { onRefresh } = useContext(ProductPackagesFetcher.Context);
  const { t } = useTranslation();

  const onSubmit = useCallback(
    async (payload: IProductPackageFormPayload) => {
      try {
        const pack = await createPackage({
          name: payload.name,
          tools: payload.tools,
          productId: payload.productId,
          baseDailyPrice: payload.baseDailyPrice,
          isWatermark: payload.isWatermark,
          isAddon: payload.isAddon,
          variants: getPackageVariantsPayload(payload),
        });
        await onRefresh();
        closeDialog(pack.id);
      } catch (e) {
        handleError(e);
      }
    },
    [createPackage]
  );

  useEffect(() => {
    form.reset({
      name: '',
      baseDailyPrice: '',
      productId: '',
      isWatermark: false,
      isAddon: packagesType === PackagesType.Addons,
      tools: [],
      durations: [],
      customDurationDays: '',
      discounts: {},
      perpetualTotalPrice: '',
    });
  }, [open, form, packagesType]);

  return (
    <Dialog open={open} onClose={closeDialog} maxWidth="md">
      <DialogTitle>
        {packagesType === PackagesType.Addons
          ? t('adminProducts.addons.addDialog.title')
          : t('adminProducts.packages.addDialog.title')}
        <Typography variant="body2" color="text.disabled" paragraph>
          {t('adminProducts.packages.addDialog.subtitle')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <ProductPackageForm form={form} onSubmit={onSubmit} />
      </DialogContent>
      <DialogActions>
        <Button onClick={closeDialog}>{t('common.dialog.close')}</Button>
        <Button variant="contained" color="primary" onClick={form.handleSubmit(onSubmit)}>
          {packagesType === PackagesType.Addons
            ? t('adminProducts.addons.addDialog.confirm')
            : t('adminProducts.packages.addDialog.confirm')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
